// EditProductPage.js - Full page edit with multi-city stock

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaUpload, FaPlus, FaTrash } from 'react-icons/fa';
import { useCurrency } from '../../context/CurrencyContext';
import Swal from 'sweetalert2';
import axios from 'axios';
import './EditProductPage.css';
import { previewProductSku } from '../../utils/skuGenerator'; // ✅ SKU generator

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk/api/product';
const MEDIA_HOST = 'https://super-backend.hira-tech.co.uk';

// backend path ya full URL, dono ko full URL bana deta hai
const resolveMediaUrl = (x) => {
  if (!x || typeof x !== 'string') return '';
  if (x.startsWith('http') || x.startsWith('data:')) return x;
  return `${MEDIA_HOST}${x.startsWith('/') ? '' : '/'}${x}`;
};

const EditProductPage = () => {
  const { shopId, productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || 'productlist';
  const { formatPrice, currency } = useCurrency();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shopCities, setShopCities] = useState([]);
  const [allTypes, setAllTypes] = useState([]);
  const [allVariations, setAllVariations] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allBrands, setAllBrands] = useState([]);

  const [formData, setFormData] = useState({
    name: '', description: '', sku: '', price: '', stock: '', packStatus: 'Yes',
    type: '', category: '', variation: '', implantCompany: '', implantConnection: '',
    reference: '', rawMaterial: '', features: '',
    gtin: '', gtinImagePreview: '', gtinImageFile: null, // ✅ GTIN + barcode image
    cityStockRows: [{ city: '', stock: '' }],
    imagePreviews: ['', '', '', ''],
    imageFiles: [null, null, null, null],
  });

  const [modalTypes, setModalTypes] = useState([]);
  const [modalVariations, setModalVariations] = useState([]);
  const [modalBrands, setModalBrands] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [pRes, cRes, tRes, vRes, compRes, catRes, bRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/products/${productId}/`),
          axios.get(`${API_BASE_URL}/shop-cities/?shop_id=${shopId}`),
          axios.get(`${API_BASE_URL}/types/?shop_id=${shopId}`),
          axios.get(`${API_BASE_URL}/variations/?shop_id=${shopId}`),
          axios.get(`${API_BASE_URL}/implant-companies/?shop_id=${shopId}`),
          axios.get(`${API_BASE_URL}/categories/?shop_id=${shopId}`),
          axios.get(`${API_BASE_URL}/brands/?shop_id=${shopId}`),
        ]);

        const p = pRes.data;
        setShopCities(cRes.data.cities || []);
        setAllTypes(tRes.data || []);
        setAllVariations(vRes.data || []);
        setAllCompanies(compRes.data || []);
        setAllCategories(catRes.data || []);
        setAllBrands(bRes.data || []);

        let cityStocks = [];
        if (Array.isArray(p.city_stocks) && p.city_stocks.length > 0) cityStocks = p.city_stocks;
        else if (p.city) cityStocks = [{ city: p.city, stock: p.product_Stock || 0 }];
        if (cityStocks.length === 0) cityStocks = [{ city: '', stock: '' }];

        const gal = p.imageGallery || {};
        const previews = [
          gal.image1_url || gal.image1 || '',
          gal.image2_url || gal.image2 || '',
          gal.image3_url || gal.image3 || '',
          gal.image4_url || gal.image4 || ''
        ].map(x => x ? (x.startsWith('http') ? x : `https://super-backend.hira-tech.co.uk${x.startsWith('/') ? '' : '/'}${x}`) : '');

        const catId = p.product_category?.catrgort_id?.toString() || '';
        const typeId = p.type?.type_id?.toString() || '';
        const compId = p.implant_company?.company_id?.toString() || '';
        const brandId = p.product_brand?.brand_id?.toString() || '';
        const varId = p.product_variation?.variation_id?.toString() || '';

        setFormData({
          name: p.product_Name || '',
          description: p.product_Description || '',
          sku: p.SKU || '',
          price: p.product_Price?.toString() || '',
          stock: p.product_Stock?.toString() || '',
          packStatus: 'Yes',
          type: typeId,
          category: catId,
          variation: varId,
          implantCompany: compId,
          implantConnection: brandId,
          reference: p.reference || '',
          rawMaterial: p.rawMaterial || '',
          features: p.features || '',
          gtin: p.gtin || '',
          gtinImagePreview: resolveMediaUrl(p.gtin_image),
          gtinImageFile: null,
          cityStockRows: cityStocks.map(cs => ({ city: cs.city, stock: String(cs.stock ?? '') })),
          imagePreviews: previews,
          imageFiles: [null, null, null, null],
        });

        setModalTypes(tRes.data || []);
        setModalVariations(vRes.data || []);
        if (compId) setModalBrands((bRes.data || []).filter(b => b.implant_company && String(b.implant_company.company_id) === String(compId)));
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load product' });
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [productId, shopId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // SKU dynamically update karein agar variation change ho
    if (name === 'variation') {
      const variation = allVariations.find(v => String(v.variation_id) === String(value));
      const type = allTypes.find(t => String(t.type_id) === String(formData.type));
      const cat = allCategories.find(c => String(c.catrgort_id) === String(formData.category));

      const newSku = previewProductSku({
        categoryName: cat?.category_Name || '',
        typeName: type?.type_name || '',
        variationName: variation?.variation_name || ''
      });

      setFormData(prev => ({ ...prev, variation: value, sku: newSku }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (i, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const prevs = [...formData.imagePreviews];
      prevs[i] = reader.result;
      const files = [...formData.imageFiles];
      files[i] = file;
      setFormData(prev => ({ ...prev, imagePreviews: prevs, imageFiles: files }));
    };
    reader.readAsDataURL(file);
  };

  // ✅ GTIN barcode image upload
  const handleGtinImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, gtinImagePreview: reader.result, gtinImageFile: file }));
    };
    reader.readAsDataURL(file);
  };

  const updateCityRow = (i, field, value) => {
    setFormData(prev => {
      const rows = [...prev.cityStockRows];
      rows[i] = { ...rows[i], [field]: value };
      return { ...prev, cityStockRows: rows };
    });
  };

  const addCityRow = () => setFormData(prev => ({ ...prev, cityStockRows: [...prev.cityStockRows, { city: '', stock: '' }] }));
  const removeCityRow = (i) => setFormData(prev => ({ ...prev, cityStockRows: prev.cityStockRows.filter((_, idx) => idx !== i) }));

  // Category change + SKU update
  const handleCategoryChange = (e) => {
    const id = e.target.value;
    const cat = allCategories.find(c => String(c.catrgort_id) === String(id));
    const type = allTypes.find(t => String(t.type_id) === String(formData.type));
    const variation = allVariations.find(v => String(v.variation_id) === String(formData.variation));

    const newSku = previewProductSku({
      categoryName: cat?.category_Name || '',
      typeName: type?.type_name || '',
      variationName: variation?.variation_name || ''
    });

    setFormData(prev => ({
      ...prev,
      category: id,
      type: '', // reset dependent fields if you want
      variation: '',
      sku: newSku
    }));
    setModalTypes(allTypes);
    setModalVariations(allVariations);
  };

  // Type change + SKU update
  const handleTypeChange = (e) => {
    const id = e.target.value;
    const type = allTypes.find(t => String(t.type_id) === String(id));
    const cat = allCategories.find(c => String(c.catrgort_id) === String(formData.category));
    const variation = allVariations.find(v => String(v.variation_id) === String(formData.variation));

    const newSku = previewProductSku({
      categoryName: cat?.category_Name || '',
      typeName: type?.type_name || '',
      variationName: variation?.variation_name || ''
    });

    setFormData(prev => ({
      ...prev,
      type: id,
      variation: '', // reset variation if you want
      sku: newSku
    }));
    setModalVariations(allVariations);
  };

  const handleCompanyChange = (e) => {
    const id = e.target.value;
    setFormData(prev => ({ ...prev, implantCompany: id, implantConnection: '' }));
    setModalBrands(id ? allBrands.filter(b => b.implant_company && String(b.implant_company.company_id) === String(id)) : []);
  };

  const handleUpdate = async () => {
    const rows = formData.cityStockRows.filter(r => r.city && r.stock !== '');
    if (rows.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Missing City', text: 'Add at least one city with stock' });
      return;
    }

    setSaving(true);
    try {
      const cityStocks = rows.map(r => ({ city: r.city.trim(), stock: parseInt(r.stock) || 0 }));
      const total = cityStocks.reduce((s, c) => s + c.stock, 0);

      const fd = new FormData();
      fd.append('product_Name', formData.name);
      fd.append('product_Description', formData.description);
      fd.append('product_Price', parseFloat(formData.price) || 0);
      fd.append('product_Stock', total);
      fd.append('city_stocks', JSON.stringify(cityStocks));
      fd.append('city', cityStocks[0].city);
      fd.append('shop_id', shopId);
      fd.append('product_isNew', false);

      if (formData.sku) fd.append('SKU', formData.sku);
      if (formData.reference) fd.append('reference', formData.reference);
      if (formData.rawMaterial) fd.append('rawMaterial', formData.rawMaterial); // ✅ Raw Material
      if (formData.features) fd.append('features', formData.features);          // ✅ Features
      if (formData.gtin && formData.gtin.trim()) fd.append('gtin', formData.gtin.trim()); // ✅ GTIN
      if (formData.gtinImageFile) fd.append('gtin_image', formData.gtinImageFile);        // ✅ GTIN barcode image

      const cat = allCategories.find(c => String(c.catrgort_id) === String(formData.category));
      if (cat) fd.append('category_Name', cat.category_Name);
      const typ = allTypes.find(t => String(t.type_id) === String(formData.type));
      if (typ) fd.append('type_name', typ.type_name);
      const v = allVariations.find(x => String(x.variation_id) === String(formData.variation));
      if (v) fd.append('variation_name', v.variation_name);
      const comp = allCompanies.find(c => String(c.company_id) === String(formData.implantCompany));
      if (comp) fd.append('implant_Company', comp.company_name);
      const br = allBrands.find(b => String(b.brand_id) === String(formData.implantConnection));
      if (br) fd.append('brand_Name', br.brand_name);

      formData.imageFiles.forEach((f, i) => { if (f) fd.append(`image${i + 1}`, f); });

      await axios.put(`${API_BASE_URL}/products/${productId}/update/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      Swal.fire({ icon: 'success', title: 'Updated!', text: 'Product updated successfully', timer: 1500, showConfirmButton: false });
      setTimeout(() => navigate(`/shop/${shopId}/${from}`), 1200);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.error || 'Failed to update' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="edit-product-loading">Loading product...</div>;

  return (
    <div className="edit-product-page">
      <div className="edit-product-header">
        <button className="back-btn" onClick={() => navigate(`/shop/${shopId}/${from}`)}>
          <FaArrowLeft /> Back
        </button>
        <h2>Edit Product</h2>
        <button className="update-btn" onClick={handleUpdate} disabled={saving}>
          <FaSave /> {saving ? 'Saving...' : 'Update Product'}
        </button>
      </div>

      <div className="edit-product-layout">
        <div className="edit-col images-col">
          {[0, 1, 2, 3].map(i => (
            <div className="image-preview-box" key={i}>
              <div className="image-preview-label">Image {i + 1} Preview</div>
              {formData.imagePreviews[i] ? (
                <img src={formData.imagePreviews[i]} alt={`preview ${i + 1}`} className="image-preview-img" />
              ) : (
                <div className="image-preview-empty">No Image</div>
              )}
              <label className="image-upload-circle">
                <FaUpload />
                <input type="file" accept="image/*" onChange={(e) => handleImageUpload(i, e)} style={{ display: 'none' }} />
              </label>
            </div>
          ))}
        </div>

        <div className="edit-col preview-col">
          <h3 className="preview-title">Product Preview</h3>
          <div className="preview-name">{formData.name || 'Product Name'}</div>
          <div className="preview-rows">
            <div className="preview-row"><span>Status:</span><span>{formData.packStatus}</span></div>
            <div className="preview-row"><span>Price:</span><span>{formData.price ? formatPrice(parseFloat(formData.price)) : '-'}</span></div>
            <div className="preview-row"><span>Stock:</span><span>{formData.cityStockRows.reduce((s, r) => s + (parseInt(r.stock) || 0), 0)}</span></div>
            <div className="preview-row"><span>Type:</span><span>{allTypes.find(t => String(t.type_id) === String(formData.type))?.type_name || '-'}</span></div>
            <div className="preview-row"><span>Category:</span><span>{allCategories.find(c => String(c.catrgort_id) === String(formData.category))?.category_Name || '-'}</span></div>
            <div className="preview-row"><span>Variation:</span><span>{allVariations.find(v => String(v.variation_id) === String(formData.variation))?.variation_name || '-'}</span></div>
            <div className="preview-row"><span>Implant Company:</span><span>{allCompanies.find(c => String(c.company_id) === String(formData.implantCompany))?.company_name || '-'}</span></div>
            <div className="preview-row"><span>Implant Connection:</span><span>{allBrands.find(b => String(b.brand_id) === String(formData.implantConnection))?.brand_name || '-'}</span></div>
            <div className="preview-row"><span>SKU:</span><span>{formData.sku || '-'}</span></div>
            <div className="preview-row"><span>GTIN:</span><span>{formData.gtin || '-'}</span></div>
          </div>
          <div className="preview-description-title">Description:</div>
          <div className="preview-description">{formData.description || '-'}</div>
        </div>

        <div className="edit-col form-col">
          <div className="form-group">
            <label>Product Name</label>
            <input type="text" name="name" value={formData.name} onChange={handleChange} />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea name="description" rows="5" value={formData.description} onChange={handleChange} />
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>SKU</label>
              <input type="text" name="sku" value={formData.sku} readOnly />
            </div>
            <div className="form-group">
              <label>Price ({currency})</label>
              <input type="number" name="price" value={formData.price} onChange={handleChange} />
            </div>
          </div>

          <div className="form-group">
            <label>Cities &amp; Stock</label>
            <div className="city-stock-rows">
              {formData.cityStockRows.map((row, i) => (
                <div className="city-stock-row" key={i}>
                  <select value={row.city} onChange={(e) => updateCityRow(i, 'city', e.target.value)}>
                    <option value="">Select City</option>
                    {shopCities.map((c, idx) => <option key={idx} value={c}>{c}</option>)}
                  </select>
                  <input
                    type="number"
                    placeholder="Stock"
                    value={row.stock}
                    onChange={(e) => updateCityRow(i, 'stock', e.target.value)}
                  />
                  {i === formData.cityStockRows.length - 1 ? (
                    <button type="button" className="city-btn add" onClick={addCityRow}><FaPlus /></button>
                  ) : (
                    <button type="button" className="city-btn remove" onClick={() => removeCityRow(i)}><FaTrash /></button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Pack Status</label>
              <select name="packStatus" value={formData.packStatus} onChange={handleChange}>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>
            <div className="form-group">
              <label>Type (optional)</label>
              <select value={formData.type} onChange={handleTypeChange}>
                <option value="">No Type</option>
                {modalTypes.map(t => <option key={t.type_id} value={t.type_id}>{t.type_name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Category (optional)</label>
              <select value={formData.category} onChange={handleCategoryChange}>
                <option value="">No Category</option>
                {allCategories.map(c => <option key={c.catrgort_id} value={c.catrgort_id}>{c.category_Name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Variation (optional)</label>
              <select name="variation" value={formData.variation} onChange={handleChange}>
                <option value="">No Variation</option>
                {modalVariations.map(v => <option key={v.variation_id} value={v.variation_id}>{v.variation_name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Implant Company</label>
              <select value={formData.implantCompany} onChange={handleCompanyChange}>
                <option value="">Select Company</option>
                {allCompanies.map(c => <option key={c.company_id} value={c.company_id}>{c.company_name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Implant Connection</label>
              <select name="implantConnection" value={formData.implantConnection} onChange={handleChange} disabled={!formData.implantCompany}>
                <option value="">Select Brand</option>
                {modalBrands.map(b => <option key={b.brand_id} value={b.brand_id}>{b.brand_name}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Reference</label>
              <input type="text" name="reference" value={formData.reference} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Raw Material</label>
              <input type="text" name="rawMaterial" value={formData.rawMaterial} onChange={handleChange} />
            </div>
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label>Features</label>
              <input type="text" name="features" value={formData.features} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>GTIN</label>
              <input type="text" name="gtin" value={formData.gtin} onChange={handleChange} placeholder="e.g. 1234567891234" />
            </div>
          </div>

          <div className="form-group">
            <label>GTIN Barcode Image</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {formData.gtinImagePreview ? (
                <img
                  src={formData.gtinImagePreview}
                  alt="GTIN barcode"
                  style={{ width: 70, height: 70, objectFit: 'contain', border: '1px solid #d6dbe4', borderRadius: 6, background: '#fff' }}
                />
              ) : (
                <div
                  style={{ width: 70, height: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #c3cad6', borderRadius: 6, color: '#94a3b8', fontSize: 12 }}
                >
                  No Image
                </div>
              )}
              <label className="image-upload-circle" title="Upload GTIN barcode image">
                <FaUpload />
                <input type="file" accept="image/*" onChange={handleGtinImageUpload} style={{ display: 'none' }} />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProductPage;